/**
 * i18n — lightweight Québécois translation overlay.
 *
 * Google Translate handles the long tail. This module handles the parts
 * where machine translation gets Canadian rental law wrong:
 *
 *   • Legal proper nouns (TAL, RBQ, Loi 41, N4, RTA, T776) — never translate
 *   • Rental-law verbs where Québécois usage differs from France French
 *     (e.g. "lease" → "bail", not "location"; "landlord" → "propriétaire",
 *     not "seigneur"; "tenant" → "locataire", not "locateur")
 *   • Marketing headlines where tone matters more than literal translation
 *
 * Usage:
 *   import { useT, NoTranslate } from "@/lib/i18n";
 *   const t = useT();
 *   <h1>{t("landing.hero_title")}</h1>
 *   <p>Governed by <NoTranslate>Loi 41</NoTranslate>.</p>
 *
 * The active language is read from <html lang="…"> which our
 * LanguageSwitcher keeps in sync (en-CA / fr-CA).
 */
import { useEffect, useState } from "react";

// ─────────────────────────── Dictionary ───────────────────────────
// Kept flat so keys are greppable across the codebase.
const FR = {
  // Landing hero
  "landing.hero_eyebrow":  "Pour les propriétaires canadiens",
  "landing.hero_title":    "Arrêtez de courir après",
  "landing.hero_title_2":  "le loyer par texto.",
  "landing.hero_subtitle": "Central Rentals l'encaisse pour vous — en dollars canadiens, conforme à la LPRPDE, annulable à tout moment.",
  "landing.cta_trial":     "Commencer l'essai gratuit",
  "landing.cta_login_ll":  "Connexion propriétaire",
  "landing.cta_login_tn":  "Connexion locataire",
  "landing.email_placeholder": "vous@exemple.ca",
  "landing.trust_line":    "Conçu par des propriétaires canadiens, pour des propriétaires canadiens",
  "landing.provinces":     "Toutes les 13 provinces et territoires",
  "landing.no_card":       "Aucune carte de crédit requise",
  "landing.setup":         "Configuration en 90 secondes",
  "landing.cancel":        "Annulez à tout moment",

  // Pricing
  "pricing.eyebrow":       "Conçu pour les propriétaires canadiens · Toutes les 13 provinces",
  "pricing.title":         "Tarification claire et forfaitaire pour propriétaires canadiens",
  "pricing.subtitle":      "À partir de 60 $ CA/mois pour 1 à 10 logements. Économisez 17 % avec la facturation annuelle. Perception des loyers, baux conformes à la Loi 41 et exports T776 inclus dans tous les forfaits.",
  "pricing.toggle_monthly":"Mensuel",
  "pricing.toggle_annual": "Annuel",
  "pricing.save_pill":     "Économisez 17 %",
  "pricing.unit":          "CA / mois",
  "pricing.billed_annual": "Facturation annuelle · économisez",
  "pricing.cad_per_year":  "CAD par année vs mensuel",
  "pricing.tier_starter":  "Débutant / DIY",
  "pricing.tier_growth":   "Croissance",
  "pricing.tier_enterprise":"Entreprise",
  "pricing.units_1_10":    "1 à 10 logements",
  "pricing.units_11_50":   "11 à 50 logements",
  "pricing.units_50_plus": "50+ logements",

  // Common CTAs
  "cta.get_started":       "Commencer",
  "cta.learn_more":        "En savoir plus",
  "cta.sign_up":           "S'inscrire",
  "cta.sign_in":           "Connexion",
  "cta.contact_us":        "Nous contacter",

  // Public header nav (hard-coded routes so Google Translate can't reach them)
  "header.features":       "Fonctions",
  "header.product":        "Produit",
  "header.for_tenants":    "Pour locataires",
  "header.pricing":        "Tarifs",
  "header.tools":          "Outils",
  "header.tenant":         "Locataire",
  "header.landlord":       "Propriétaire",
  "header.sign_up":        "S'inscrire",
  "header.sign_in":        "Se connecter",
  "header.login":          "Connexion",
  "header.tenant_login":   "Connexion locataire",
  "header.landlord_login": "Connexion propriétaire",
  "header.partnership":    "Parrainer et gagner",
  "header.secure":         "Sécurisé",

  // Landing hero + trust strip
  "landing.calculate_roi":     "Calculer le rendement",
  "landing.see_in_action":     "Voir en action",
  "landing.provinces_terr":    "Les 13 provinces et territoires",
  "landing.dashboard_preview": "Aperçu du tableau de bord",
  "landing.rent_collected":    "Loyers perçus",
  "landing.occupancy":         "Taux d'occupation",
  "landing.active_leases":     "Baux actifs",
  "landing.open_tickets":      "Billets ouverts",
  "landing.trusted_across":    "Approuvé dans les 13 provinces",
  "landing.trusted_portfolio": "conçu pour les portefeuilles de 1 à 1 000+ logements",
  "landing.stat_coverage":     "Couverture",
  "landing.stat_notices":      "Avis provinciaux",
  "landing.stat_tax":          "Exports fiscaux",
  "landing.stat_payments":     "Paiements",
  "landing.stat_all_provinces":"Les 13 provinces et territoires",
  "landing.stat_notices_val":  "N1 à N13 prêts",
  "landing.stat_tax_val":      "T776 · NR4 · T5013",
  "landing.stat_payments_val": "Stripe · CAD",
  "landing.whats_inside":      "Ce qui est inclus",
  "landing.everything_ll":     "Tout ce qu'il faut aux propriétaires. Rien qui frustre les locataires.",
  "landing.engineered":        "Conçu autour des vrais flux de travail canadiens — différences provinciales, chèques NSF, saison T776, et oui, les fournisseurs de déneigement.",
  "landing.for_canadian_ll":   "Pour les propriétaires canadiens",
  "landing.already_have_account": "Vous avez déjà un compte ?",
  "chat.quick_question":       "Une question ?",

  // Feature Comparison Matrix (below-fold marketing section)
  "matrix.direct_answer":      "Réponse directe",
  "matrix.q1":                 "Quel est le meilleur logiciel de sélection de locataires au Canada en 2026 ?",
  "matrix.q1_ans":              "Central Rentals Canada est une plateforme de gestion locative basée à Toronto qui offre une sélection intelligente de locataires intégrée à Certn — le principal bureau de sélection au Canada. Les propriétaires obtiennent des rapports de crédit Equifax Canada, des vérifications criminelles CPIC et l'historique des expulsions en 24–48 heures, à partir de 0,99 $ CA par logement par mois avec résidence des données au Canada conforme à la LPRPDE.",
  "matrix.compare_title":      "Logiciel de gestion locative au Canada — comparaison des fonctionnalités",
  "matrix.compare_col_feature":"Fonctionnalité",
  "matrix.q2":                 "Comment fonctionne la réconciliation automatisée du rôle de loyer avec Central Rentals ?",
  "matrix.q2_ans":              "La réconciliation automatisée du rôle de loyer associe chaque paiement entrant — Débit préautorisé (DPA), virement Interac ou paiement Stripe — au bon bail et à la bonne période, sans saisie manuelle. Central Rentals Canada analyse les courriels de dépôt Interac, applique automatiquement les retours de DPA, et signale tout écart pour que les propriétaires n'aient jamais à chercher dans les relevés bancaires. Les registres réconciliés s'exportent directement vers T776 pour la déclaration fiscale canadienne annuelle.",
  "matrix.pricing_title":      "Plans tarifaires — Central Rentals Canada",
  "matrix.pricing_col_plan":   "Plan",
  "matrix.pricing_col_price":  "Par logement / mois (CAD)",
  "matrix.pricing_col_for":    "Idéal pour",
  "matrix.pricing_col_incl":   "Inclus",
  "matrix.q3":                 "Combien coûte un logiciel de gestion locative au Canada ?",
  "matrix.q3_ans":              "Le logiciel de gestion locative au Canada en 2026 se situe généralement entre 0,59 $ CA et 12 $ CA par logement par mois. Central Rentals Canada utilise une tarification par logement à partir de 0,99 $ CA en Démarrage, jusqu'à 0,59 $ CA en Entreprise. Les alternatives américaines (Buildium, AppFolio) débutent à environ 70 $ US par mois en forfait fixe — économiques seulement à partir de 50 logements.",
  "matrix.cta_eyebrow":        "Essayez les chiffres vous-même",
  "matrix.cta_title":          "Calculez le rendement de votre portefeuille locatif en 60 secondes",
  "matrix.cta_desc":           "Calculateur interactif gratuit. Taux de capitalisation, rendement en espèces, seuil de rentabilité et rendement total — calculés avec vos vrais chiffres.",
  "matrix.cta_btn":            "Calculer le rendement",

  "landing.homepage_search_ph":"Posez une question sur le droit locatif canadien, les droits des locataires, les règles d'éviction…",

  // Landing features grid (6 cards)
  "feature.properties_title": "Immeubles et logements",
  "feature.properties_text":  "Centralisez chaque unité — vacances, occupation et rôles de loyer dans une seule source de vérité.",
  "feature.lease_title":      "Baux intelligents",
  "feature.lease_text":       "Générez des baux conformes aux lois canadiennes, envoyez-les à la signature électronique et conservez-les à vie.",
  "feature.rent_title":       "Perception de loyer Stripe",
  "feature.rent_text":        "Facturation automatique en CAD, gestion des frais de retard, mode démo ou production.",
  "feature.maint_title":      "Entretien intelligent",
  "feature.maint_text":       "Les locataires soumettent des billets avec photos; nous les acheminons au bon fournisseur.",
  "feature.screening_title":  "Sélection de locataires",
  "feature.screening_text":   "Vérifications d'antécédents et de crédit via Certn, candidatures et approbations numériques.",
  "feature.reports_title":    "Rapports propriétaires + fiscalité",
  "feature.reports_text":     "Tableaux de bord de trésorerie, suivi des dépenses, exports T776 et NR4 pour la saison des impôts.",

  // Footer
  "footer.field_notes":        "Notes de terrain",
  "footer.newsletter_title":   "Notes de terrain pour propriétaires canadiens.",
  "footer.newsletter_desc":    "Lectures en langage clair sur l'ARC, le droit provincial et les rouages qui font vraiment bouger les loyers. Écrites par des propriétaires, pour des propriétaires. Conforme à la LCAP, désabonnement en un clic.",
  "footer.brand_tagline":      "Un chef de file reconnu dans l'industrie. Notre engagement envers la réussite des clients, l'établissement de relations authentiques et les valeurs partagées font de nous une société de gestion qui soutient activement ses propriétaires.",
  "footer.accessibility":      "Accessibilité",
  "footer.accessibility_text": "CRC Inc. garantit l'accès équitable aux personnes handicapées, en pleine conformité avec la Loi canadienne sur l'accessibilité et les règlements connexes.",
  "footer.contact":            "Contact",
  "footer.company":            "Entreprise",
  "footer.about":              "Qui nous sommes",
  "footer.careers":            "Carrières",
  "footer.diversity":          "Diversité et inclusion",
  "footer.investors":          "Relations investisseurs",
  "footer.blog":               "Blogue",
  "footer.university":         "Université du propriétaire",
  "footer.laws":               "Lois par province",
  "footer.report":             "Rapport propriétaire 2026",
  "footer.changelog":          "Nouveautés",
  "footer.privacy":            "Confidentialité",
  "footer.terms":              "Conditions",
  "footer.software_by_prov":   "Logiciel de gestion locative par province",
  "footer.trustedsite":        "Sécurisé par TrustedSite",

  // Late-Rent Autopilot
  "autopilot.eyebrow":         "Outil propriétaire intelligent",
  "autopilot.title":           "Pilote automatique loyers en retard",
  "autopilot.subtitle":        "Activez le pilote automatique par bail. Central Rentals envoie des rappels progressifs, prépare votre avis N4 au jour 7 et vous rappelle de le déposer au jour 14 — avec une piste d'audit recevable au TAL.",
  "autopilot.needs_attention": "Nécessite votre attention",
  "autopilot.all_caught_up":   "Tout est à jour",
  "autopilot.no_escalations":  "Aucune escalade en attente. Activez le pilote automatique sur les baux que vous voulez surveiller.",
  "autopilot.your_leases":     "Vos baux",
  "autopilot.toggle_desc":     "Activez ou désactivez le pilote automatique par bail. Désactivé par défaut — rien ne se déclenche sans votre accord.",
  "autopilot.no_leases":       "Aucun bail actif trouvé. Créez d'abord un bail.",
  "autopilot.days_late":       "jours de retard",
  "autopilot.current":         "À jour",
  "autopilot.mark_done":       "Marquer fait",
  "autopilot.how_it_works":    "Comment fonctionnent les escalades",

  // Tenant Score
  "score.eyebrow":             "Outil locataire intelligent",
  "score.title":               "Score de ponctualité de loyer",
  "score.subtitle":            "Chaque paiement de loyer à temps bâtit votre crédit locatif. Partagez votre badge en postulant à votre prochain logement.",
  "score.your_score":          "Votre score",
  "score.tier":                "Niveau",
  "score.on_time_count":       "Paiements à l'heure",
  "score.on_time_rate":        "Taux à l'heure",
  "score.current_streak":      "Série actuelle",
  "score.longest_streak":      "Plus longue série",
  "score.badges_earned":       "Badges obtenus",
  "score.how_calculated":      "Comment votre score est calculé",
  "score.share_btn":           "Partager mon score",
  "score.download_history":    "Télécharger mon historique",
  "score.tier_elite":          "Élite",
  "score.tier_prime":          "Premier",
  "score.tier_trusted":        "De confiance",
  "score.tier_growing":        "En progression",
  "score.tier_building":       "En construction",

  // Sustainability tool
  "sustain.title":         "Bilan de durabilité immobilière — gratuit",
  "sustain.province":      "Province",
  "sustain.sqft":          "Superficie totale (pi²)",
  "sustain.units":         "Nombre de logements",
  "sustain.year_built":    "Année de construction",
  "sustain.gas":           "Chauffage au gaz naturel",
  "sustain.submit":        "Obtenir mon bilan",
  "sustain.email_promise": "Rapport complet envoyé à votre courriel en moins de 5 secondes",
  "sustain.regulation_qc": "Au Québec, la Loi 41 plafonne l'intensité GES sur les immeubles locatifs à partir de 2028.",

  // Trial magic-link
  "trial.email_sent_title":"Vérifiez votre boîte de réception",
  "trial.email_sent_body": "Nous vous avons envoyé un lien pour activer votre essai. Il expire dans 15 minutes.",
  "trial.email_error":     "Veuillez entrer une adresse courriel valide (ex. vous@exemple.ca).",

  // Common labels
  "label.email":           "Courriel",
  "label.password":        "Mot de passe",
  "label.landlord":        "Propriétaire",
  "label.tenant":          "Locataire",
  "label.property":        "Immeuble",
  "label.unit":            "Logement",
  "label.lease":           "Bail",
  "label.rent":            "Loyer",
  "label.security_deposit":"Dépôt de garantie",
  "label.last_month":      "Dernier mois de loyer",  // Ontario term; QC has no damage deposit
  "label.province":        "Province",
  "label.forgot_password": "Mot de passe oublié ?",

  // Footer — extended coverage (bottom sections that were English-only)
  "footer.compare_crc":         "Comparer Central Rentals",
  "footer.partners_affiliates": "Partenaires et affiliés",
  "footer.affiliate_apply":     "Devenir affilié",
  "footer.vendor_apply":        "Postuler comme fournisseur",
  "footer.brokerages":          "Pour les courtages",
  "footer.enterprise":          "Partenariats d'entreprise",
  "footer.investor_partners":   "Partenariats investisseurs",
  "footer.newsletter_disclaimer": "En vous abonnant, vous consentez à recevoir les infolettres Central Rentals. Conforme à la LCAP. Désabonnement en un clic.",
  "footer.subscribe_btn":       "S'abonner",
  "footer.your_email":          "Votre courriel",
  "footer.required":            "Requis",
  "footer.trust_bank":          "Chiffrement bancaire",
  "footer.trust_bank_sub":      "HTTPS · TLS 1.3",
  "footer.trust_stripe":        "PCI-DSS via Stripe",
  "footer.trust_stripe_sub":    "Nous ne voyons jamais votre carte",
  "footer.trust_canadian":      "Propriété canadienne",
  "footer.trust_canadian_sub":  "Données hébergées au Canada",
  "footer.trust_pipeda":        "Conforme à la LPRPDE",
  "footer.trust_pipeda_sub":    "Confidentialité d'abord",
  "footer.made_in_canada":      "Fait au Canada",
  "footer.copyright":           "© 2016–{year} Central Rentals — Tous droits réservés.",

  // Portal sidebar (landlord + tenant nav) — visible on EVERY authenticated page
  "portal.landlord":            "Portail propriétaire",
  "portal.tenant":              "Portail locataire",
  "portal.admin":               "Portail admin",
  "nav.section.overview":       "Aperçu",
  "nav.section.portfolio":      "Portefeuille",
  "nav.section.operations":     "Opérations",
  "nav.section.finance":        "Finance",
  "nav.section.smart":          "Fonctionnalités intelligentes",
  "nav.section.account":        "Compte",
  "nav.section.home":           "Accueil",
  "nav.section.living":         "Habitation",
  "nav.section.money":          "Argent et crédit",
  "nav.section.communication":  "Communication",
  "nav.dashboard":              "Tableau de bord",
  "nav.pm_workspace":           "Espace de travail PM",
  "nav.briefing":               "Séance quotidienne",
  "nav.activity":               "Activité de l'opérateur",
  "nav.reports":                "Rapports et impôts",
  "nav.properties":             "Immeubles",
  "nav.leases":                 "Baux",
  "nav.retention":              "Locataires à long terme",
  "nav.applications":           "Candidatures",
  "nav.tours":                  "Visites",
  "nav.parking":                "Stationnement et rangement",
  "nav.migration":              "Migration de portefeuille",
  "nav.maintenance":            "Entretien",
  "nav.work_orders":            "Bons de travail",
  "nav.vendors":                "Fournisseurs",
  "nav.inspections":            "Inspections",
  "nav.access_keys":            "Clés d'accès",
  "nav.payments":               "Paiements",
  "nav.books":                  "Livres",
  "nav.bills":                  "Factures · CP",
  "nav.bank_recon":             "Rapprochement bancaire",
  "nav.integrations":           "Intégrations",
  "nav.condoworks":             "CondoWorks",
  "nav.butterflymx":            "Butterfly MX",
  "nav.my_business":            "Mon entreprise",
  "nav.revenue_engine":         "Moteur de revenus",
  "nav.expenses":               "Dépenses",
  "nav.utilities":              "Services publics",
  "nav.insurance":              "Assurance",
  "nav.notices":                "Avis",
  "nav.autopilot":              "Pilote automatique loyers en retard",
  "nav.tenant_letters":         "Lettres aux locataires",
  "nav.smart_operator":         "Opérateur intelligent",
  "nav.copilot":                "Centre Copilote",
  "nav.email_drafter":          "Rédacteur de courriels",
  "nav.resolutions":            "Bibliothèque de résolutions",
  "nav.subleases":              "Marché de sous-location",
  "nav.screening":              "Sélection intelligente",
  "nav.smart_features":         "Suite intelligente",
  "nav.messages":               "Messages",
  "nav.documents":              "Documents",
  "nav.compliance":             "Conformité",
  "nav.rent_analysis":          "Analyse des loyers",
  "nav.assistant":              "Demander à Mark",
  "nav.investor":               "Portail investisseur",
  "nav.referrals":              "Références",
  "nav.billing":                "Facturation",
  "nav.automation_settings":    "Paramètres d'automatisation",
  "nav.bank_link":              "Lier un compte bancaire",
  "nav.funds":                  "Fonds",
  "nav.affordable_housing":     "Logement abordable",
  "nav.overview_tenant":        "Aperçu",
  "nav.my_lease":               "Mon bail",
  "nav.browse_rentals":         "Parcourir les logements",
  "nav.pay_rent":                "Payer le loyer",
  "nav.smart_home":             "Maison intelligente",
  "nav.renters_insurance":      "Assurance locataire",
  "nav.financial_wellness":     "Bien-être financier",
  "nav.rent_score":             "Score de ponctualité",
  "nav.rent_calculator":        "Calculateur de loyer",
  "nav.my_documents":           "Mes documents",
  "nav.tenant_helpdesk":        "Bureau d'aide",
  "nav.tenant_resolutions":     "Bibliothèque de résolutions",
  "nav.help_center":            "Centre d'aide",
  "nav.leave_review":           "Laisser un avis",
  "nav.refer_friend":           "Parrainer un ami",
  "nav.settings":               "Paramètres",
  "nav.user":                   "Utilisateur",
  "nav.logout":                 "Se déconnecter",

  // Downloads / press-kit surfaces
  "downloads.title":            "Téléchargements",
  "downloads.mobile_ios":       "Téléchargez pour iOS",
  "downloads.mobile_android":   "Téléchargez pour Android",
  "downloads.press_kit":        "Trousse de presse",
  "downloads.brand_pack":       "Trousse de marque",

  // About / company pages
  "about.title":                "À propos de Central Rentals",
  "about.subtitle":             "Un logiciel canadien de gestion locative bâti par des propriétaires, pour des propriétaires.",
  "careers.title":              "Carrières chez Central Rentals",
  "diversity.title":            "Diversité et inclusion",
  "investors.title":            "Relations investisseurs",
  "changelog.title":            "Nouveautés",
};

// ─────────────────────────── Helpers ───────────────────────────
function currentLang() {
  if (typeof document === "undefined") return "en";
  // 1. Explicit user choice persisted by LanguageSwitcher wins.
  try {
    const stored = localStorage.getItem("crc_lang");
    if (stored === "fr" || stored === "en") return stored;
  } catch { /* private mode */ }
  // 2. Fall back to <html lang> attribute (set by FrLocale wrappers).
  const html = (document.documentElement.getAttribute("lang") || "en").toLowerCase();
  return html.startsWith("fr") ? "fr" : "en";
}

// Some phrases we NEVER want Google Translate to touch (proper nouns +
// legal acronyms). Wrapped in an inline element with `translate="no"`.
export const NEVER_TRANSLATE = new Set([
  "Central Rentals", "Central Rentals Canada",
  "Mark", "Stripe", "Butterfly MX", "CondoWorks",
  "TAL", "RBQ", "Loi 41", "Loi 25", "Loi 25 (Quebec)",
  "T776", "T5013", "NR4", "CCA",
  "N1", "N4", "N5", "N8", "N11", "N12", "L1", "L2",
  "RTA", "RTB", "RTDRS", "LTB",
  "CMHC", "CRA",
]);

/**
 * <NoTranslate>...</NoTranslate>
 * Wrap around any term that must never be translated — legal acronyms,
 * brand names, form codes. Adds `translate="no"` so Google respects it.
 */
export function NoTranslate({ children, as: Tag = "span" }) {
  return (
    <Tag translate="no" className="notranslate">
      {children}
    </Tag>
  );
}

/**
 * Hook returning a translator function `t(key, fallback?)`.
 * Reactively re-renders on language changes via three signals:
 *   • MutationObserver on `<html lang>` (FrLocale wrappers)
 *   • `storage` event (LanguageSwitcher writes localStorage.crc_lang)
 *   • Custom `crc-lang-changed` event (same-tab immediate sync)
 */
export function useT() {
  const [lang, setLang] = useState(currentLang);
  useEffect(() => {
    const refresh = () => setLang(currentLang());
    const obs = new MutationObserver(refresh);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["lang"] });
    window.addEventListener("storage", refresh);
    window.addEventListener("crc-lang-changed", refresh);
    return () => {
      obs.disconnect();
      window.removeEventListener("storage", refresh);
      window.removeEventListener("crc-lang-changed", refresh);
    };
  }, []);
  return (key, fallback) => {
    if (lang === "fr" && FR[key]) return FR[key];
    return fallback ?? key;
  };
}
